import {
  IDatePicker,
  IWedding_Website_Section_HotelsField,
} from '@bridebook/models/source/models/Weddings.types';
import { Box } from '@bridebook/ui';
import { ms } from '@bridebook/ui/src/fela-utils/ms';
import {
  WebsiteTileVariant,
  WeddingWebsiteTileComponent,
} from '@bridebook/web/components/wedding-website/view-mode/partials/tile-component/wedding-website-tile-component';
import { UrlHelper } from '@bridebook/web/lib/url-helper';
import { useDispatch } from '@bridebook/web/lib/utils';
import { interactedWithWeddingWebsiteAnalyticsButtonClick } from '@bridebook/web/lib/wedding-website/analytics';
import { HOTELS_DECORATIVE_IMAGE_URL } from '@bridebook/web/lib/wedding-website/hotels/constants';
import { Palettes } from '@bridebook/web/lib/wedding-website/types';
import { useTranslation } from 'next-i18next';
import { useCallback, useEffect, useRef } from 'react';
import { useFela } from 'react-fela';
import styles from './wedding-website-hotels.style';
import { useViewModeSuppliers } from '../../view-mode.context';
import { useHotelPlannerUrl } from '@bridebook/web/lib/wedding-website/hotels/use-hotel-planner-url';
import openInBrowser from '@bridebook/web/lib/utils/open-in-browser';
import { FloatingEditButton } from '@bridebook/web/components/wedding-website/view-mode/partials/floating-edit-button/floating-edit-button';
import { useEditorSyncContext } from '@bridebook/web/components/wedding-website/main/desktop/editor-sync-context';
import { buildImageSrc } from '@bridebook/toolbox/src/buildImageUrl';

interface IProps {
  section: IWedding_Website_Section_HotelsField;
  variant: WebsiteTileVariant;
  palette: Palettes;
  primaryVenueId?: string;
  weddingDate?: IDatePicker | null;
}

export const WeddingWebsiteHotels = ({
  variant,
  palette,
  section,
  primaryVenueId,
  weddingDate,
}: IProps) => {
  const { css } = useFela();
  const dispatch = useDispatch();
  const { t } = useTranslation('weddingWebsite');
  const { byId } = useViewModeSuppliers();
  const { registerPreviewRef, triggerDetailsSync } = useEditorSyncContext();
  const tileRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    registerPreviewRef('hotels', tileRef.current);
  }, [registerPreviewRef]);

  // Prefer the primary venue supplier if present
  const supplier = primaryVenueId ? byId[primaryVenueId] : undefined;

  const handleEditClick = useCallback(() => {
    triggerDetailsSync('hotels');
    dispatch(
      interactedWithWeddingWebsiteAnalyticsButtonClick({
        section: 'placesToStay',
        element: 'editButton',
      }),
    );
  }, [dispatch, triggerDetailsSync]);

  const hotelPlannerUrl = useHotelPlannerUrl({ supplier, weddingDate });
  const handleHotelPlannerUrlClick = useCallback(() => {
    openInBrowser(hotelPlannerUrl);
  }, [hotelPlannerUrl]);

  return (
    <div ref={tileRef} className="group w-full">
      <WeddingWebsiteTileComponent variant={variant} palette={palette} style={styles.tileComponent}>
        <Box style={styles.innerWrapper}>
          <Box style={styles.firstColumn}>
            <WeddingWebsiteTileComponent.Title style={styles.title}>
              {t('previewMode.tile.hotels.title')}
            </WeddingWebsiteTileComponent.Title>
            <img
              src={buildImageSrc(HOTELS_DECORATIVE_IMAGE_URL, { w: 320 })}
              className={css(ms(styles.image, styles.imageMobile))}
            />
            {section.data.message && (
              <WeddingWebsiteTileComponent.Text style={styles.body}>
                {section.data.message}
              </WeddingWebsiteTileComponent.Text>
            )}
            <a onClick={handleHotelPlannerUrlClick}>
              <WeddingWebsiteTileComponent.Button
                variant={palette === Palettes.Bridebook ? 'secondary' : 'primary'}
                styleWrapper={styles.button}
                disabled={!hotelPlannerUrl}>
                {t('previewMode.tile.hotels.cta')}
              </WeddingWebsiteTileComponent.Button>
            </a>
          </Box>

          <Box style={ms(styles.secondColumn, styles.imageDesktopWrapper)}>
            <img
              src={buildImageSrc(HOTELS_DECORATIVE_IMAGE_URL, { w: 320 })}
              className={css(ms(styles.image, styles.imageDesktop))}
            />
          </Box>
        </Box>
        <FloatingEditButton
          href={UrlHelper.website.weddingDetails.sectionAnchor('hotels')}
          onClick={handleEditClick}
        />
      </WeddingWebsiteTileComponent>
    </div>
  );
};
